import { EventEmitter, Injectable } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, from as observableFrom, of as observableOf } from 'rxjs';
import { catchError, flatMap, map, merge, publishReplay, refCount, scan, withLatestFrom } from 'rxjs/operators';

import { Action } from './actions';
import { FieldBase, FieldData } from './fields/field-base';
import { ImageUploadField } from './fields/image-upload-field';
import { optionalEmailValidator, passwordConfirmValidator } from './utils';
import { VFormTranslationService } from './vform-translation.service';
import {
  CatalogueData,
  ControlType,
  FormDefinition,
  FormErrorResponse,
  FormOptions,
  FormResult,
  Section,
} from './vform.models';

export interface StoreAction {
  type: Action;
  payload?: any;
}

export enum NotificationType {
  SUCCESS = 0,
  ERROR,
  WARNING,
}

export interface FormState {
  formGroup: UntypedFormGroup;
  formReady: boolean;
  sections: Section[];
  isLoading: boolean;
  isSubmitting: boolean;
  formSubmitUrl: string;
  onSubmitSuccess: boolean;
  catalogue?: CatalogueData;
}

export interface Notification {
  type: NotificationType;
  message: string;
  duration?: number;
}

export interface AssociatedControlField {
  name: string;
  control: AbstractControl;
}

@Injectable()
export class VFormStoreService {
  private action$$: BehaviorSubject<StoreAction | Observable<StoreAction>>;
  private notification$$: BehaviorSubject<Notification>;
  public formState$: Observable<FormState>;
  private _formOptions: FormOptions;
  private altersFormSubscriptions: Map<AbstractControl, Subscription>;
  private associatedControls: Map<AbstractControl, AssociatedControlField>;

  get notification$(): Observable<Notification> {
    return this.notification$$.asObservable();
  }

  get formOptions(): FormOptions {
    return this._formOptions;
  }

  set formOptions(formOptions: FormOptions) {
    this._formOptions = formOptions;
  }

  constructor(private vFormTranslationService: VFormTranslationService, private router: Router) {}

  public initStore(formOptions: FormOptions): void {
    this.formOptions = formOptions;
    this.action$$ = new BehaviorSubject({ type: 0 });
    this.notification$$ = new BehaviorSubject<Notification>(null);
    this.associatedControls = new Map<AbstractControl, AssociatedControlField>();
    this.altersFormSubscriptions = new Map<AbstractControl, Subscription>();
    this.formState$ = observableOf({
      formGroup: null,
      formReady: false,
      sections: [],
      isLoading: true,
      isSubmitting: false,
      onSubmitSuccess: false,
      formSubmitUrl: null,
    }).pipe(
      merge(this.action$$.asObservable()),
      flatMap(this.ensureObservable),
      scan(this.formStateReducer.bind(this)),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.notification$$.next({ type: NotificationType.ERROR, message: 'Unexpected error happened.' });
        console.error(err);
        return observableOf({});
      }),
    ) as any;
  }

  public clearAll(): () => void {
    return this.actionDispatcher(() => {
      return {
        type: Action.CLEAR_ALL,
        payload: {},
      };
    });
  }

  private serializeFormData(formdata: any): string {
    const formList: string[][] = [];
    for (const field in formdata) {
      // Don't create a VsourceField with anything but `source` as its field name.
      if (field === 'source') {
        formList.push([field + '=' + encodeURIComponent(formdata[field].join(','))]);
      } else if (Array.isArray(formdata[field])) {
        formdata[field].forEach((value: any) => formList.push(this.serializeField(field, value)));
      } else if (!!formdata[field] && typeof formdata[field] === 'object' && this.isCheckboxesObject(formdata[field])) {
        formList.push(this.serializeField(field, formdata[field]));
        Object.keys(formdata[field]).forEach((key) => {
          formList.push(this.serializeField(field, formdata[field][key]));
          if (formdata[field][key]) {
            formList.push(this.serializeField(field, key));
          }
        });
      } else {
        formList.push(this.serializeField(field, formdata[field]));
      }
    }
    return formList.join('&');
  }

  private isCheckboxesObject(field: any): boolean {
    for (const key in field) {
      if (typeof field[key] !== 'boolean') {
        return false;
      }
    }
    return true;
  }

  public formAlteringElementChanged(): (field: FieldBase<any>, formdata: any) => StoreAction {
    return this.actionDispatcher((field: FieldBase<any>, formdata: any) => {
      if (field.controlType === ControlType.MULTISELECT) {
        const fieldData = formdata[field.name];
        const indexOfDefault = fieldData.indexOf('_DEFAULT');
        if (fieldData.length > 1 && indexOfDefault === 0) {
          fieldData.splice(indexOfDefault, 1);
        } else if (fieldData.length < 1 || fieldData.length - 1 === indexOfDefault) {
          fieldData.splice(0, fieldData.length);
          fieldData.push('_DEFAULT');
        }
        formdata[field.name] = fieldData;
      }
      const serializedFormdata = this.serializeFormData(formdata);
      return {
        type: Action.ALTERFORM_CHANGED,
        payload: this.vFormTranslationService
          .formAlteringElementChanged(this.formOptions, field, serializedFormdata)
          .pipe(
            catchError((error) => {
              return observableOf({
                type: Action.ERROR_HANDLING,
                payload: error,
              });
            }),
            map((response: FieldData[] | { type: number; payload: string }) => {
              if (response && response.hasOwnProperty('type')) {
                return response;
              } else {
                return {
                  type: Action.UPDATE_FIELD,
                  payload: response,
                };
              }
            }),
          ),
      };
    });
  }

  public initVForm(withCredentials: boolean): (options: FormOptions) => StoreAction {
    return this.actionDispatcher((options: FormOptions) => {
      return {
        type: Action.INIT_VFORM,
        payload: this.vFormTranslationService.requestFormData(options, withCredentials).pipe(
          catchError((errorResponse) => {
            return observableOf({
              type: Action.ERROR_HANDLING,
              payload: errorResponse.error,
            });
          }),
          map((response: FormDefinition) => {
            if (response && response.hasOwnProperty('type')) {
              return response;
            }
            return this.actionDispatcher((res: FormDefinition) => this.buildFormGroup(res))(response);
          }),
          map((rawFormProperties: any) => {
            if (rawFormProperties && rawFormProperties.hasOwnProperty('type') === Action.ERROR_HANDLING) {
              return rawFormProperties;
            }
            this.applyDeferredValidators(rawFormProperties.payload.formGroup);
            return {
              type: Action.FORM_READY,
              payload: rawFormProperties.payload,
            };
          }),
        ),
      };
    });
  }

  public formSubmit(
    withCredentials: boolean,
  ): (
    formdata: any,
    successEventEmitter: EventEmitter<boolean>,
    successResultEmitter: EventEmitter<FormResult>,
  ) => StoreAction {
    return this.actionDispatcher(
      (formdata: any, successEventEmitter: EventEmitter<boolean>, successResultEmitter: EventEmitter<FormResult>) => {
        return {
          type: Action.FORM_SUBMIT,
          payload: this.formSubmitHandler(this.formOptions.formUrl, formdata, withCredentials).pipe(
            catchError((errorResponse) => {
              return observableOf({
                type: Action.ERROR_HANDLING,
                payload: errorResponse.error,
              });
            }),
            map((response) => {
              if (response && response.hasOwnProperty('type')) {
                return response;
              }
              return {
                type: Action.POST_FORMSUBMIT,
                payload: { response, successEventEmitter, successResultEmitter },
              };
            }),
          ),
        };
      },
    );
  }

  private formSubmitHandler(submitUrl: string, formdata: any, withCredentials: boolean): Observable<any> {
    let context;
    // if context is string, it would be a url with queryparams
    // we need to get rid of the duplicated fields to avoid some odd issues
    // eg. If vForm handler only accepts one marketId, which may include in both formData and queryParams, should
    //     be removed from queryParams (context)
    if (typeof this.formOptions.context === 'string') {
      context = this.getQueryParamsInContext(this.formOptions.context, formdata);
    } else {
      context = JSON.stringify(this.formOptions.context);
    }
    context = encodeURIComponent(context);
    const serializedFormData = this.serializeFormData(formdata);
    return this.vFormTranslationService.submitForm(submitUrl, serializedFormData, context, withCredentials);
  }

  private serializeField(name: string, value: string): string[] {
    if (value === null) {
      return [];
    }
    if (value !== undefined) {
      if (typeof value === 'object') {
        return [name + '=' + encodeURIComponent(JSON.stringify(value))];
      }
      return [name + '=' + encodeURIComponent(value.toString())];
    }
    return [];
  }

  public applyDeferredValidators(formGroup: UntypedFormGroup): void {
    const associatedControlsIterator = this.associatedControls.keys();
    let currentKey = associatedControlsIterator.next();
    while (!currentKey.done) {
      const associatedFormControlField = this.associatedControls.get(currentKey.value);
      if (associatedFormControlField.name === 'confirmPassword') {
        formGroup.setValidators(passwordConfirmValidator('password', 'confirmPassword'));
      }
      currentKey = associatedControlsIterator.next();
    }
  }

  public buildFormGroup(formDef: FormDefinition): StoreAction {
    // Will be redo, this structure sucks
    // field value should not be BehaviourSubject
    if (!formDef || formDef.sections.length === 0) {
      return;
    }
    const group: { [key: string]: UntypedFormControl | UntypedFormArray | UntypedFormGroup } = {};
    formDef.sections.forEach((section: Section) =>
      section.fields.forEach((field: FieldBase<any> | ImageUploadField) => {
        switch (field.controlType) {
          case ControlType.MULTITEXT: {
            const f: UntypedFormControl[] = [];
            if (!field.value) {
              field.value = '';
            }
            field.value.forEach((v: any) => {
              f.push(new UntypedFormControl(v));
            });
            group[field.name] = new UntypedFormArray(f);
            break;
          }
          case ControlType.CHECKBOXES: {
            const cf: Record<string, UntypedFormControl> = {};
            if (!field.value) {
              field.value = '';
            }
            field.options.forEach((v: any) => {
              cf[v.label] = new UntypedFormControl(field.selected.indexOf(v.value) > -1);
            });
            group[field.name] = new UntypedFormGroup(cf);
            break;
          }
          case ControlType.MULTISELECT: {
            field.options = field.options.map((option: any) => {
              return { name: option.label, value: option.value };
            });
            group[field.name] = new UntypedFormControl(field.value);
            break;
          }
          case ControlType.IMAGE: {
            if (field instanceof ImageUploadField) {
              const uploadUrl = field.uploadUrl || `${this.formOptions.formUrl}?__vformcmd__=fileupload`;
              field.customMediaUploader = {
                upload: this.vFormTranslationService.fileUploader.bind(
                  this.vFormTranslationService,
                  field.uploadData,
                  uploadUrl,
                ),
              };
              field.uploadUrl = uploadUrl;
              group[field.name] = new UntypedFormControl(field.value);
            }
            break;
          }
          default: {
            group[field.name] = new UntypedFormControl(field.value);
            if (field.name === 'confirmPassword' && group['password']) {
              this.associatedControls.set(group[field.name], {
                control: group['password'],
                name: 'confirmPassword',
              });
            }
            this.applyValidationSettings(group[field.name], field);
          }
        }
        if (field.altersForm) {
          this.subscribeToAltersFormValue(group[field.name], field);
        }
      }),
    );

    return {
      type: Action.BUILD_FORMGROUP,
      payload: {
        formGroup: new UntypedFormGroup(group),
        sections: formDef.sections,
        catalogue: formDef.catalogue,
      },
    };
  }

  public applyValidationSettings(
    formControl: UntypedFormControl | UntypedFormArray | AbstractControl,
    field: FieldBase<any>,
  ): void {
    const validators: ValidatorFn[] = [];
    if (!field.enabled || field.locked) {
      formControl.setValidators([]);
      formControl.disable();
      formControl.updateValueAndValidity();
      formControl.setValidators(validators);
      return;
    } else {
      formControl.enable();
    }
    if (field.required) {
      validators.push(Validators.required);
    }
    // Exclude date comparison
    if (typeof field.min !== 'string' && (field.min === 0 || field.min) && field.datatype !== 'string') {
      validators.push(Validators.min(field.min));
    }
    if (typeof field.max !== 'string' && (field.max === 0 || field.max) && field.datatype !== 'string') {
      validators.push(Validators.max(field.max));
    }

    // Length of string validations
    if (typeof field.min !== 'string' && (field.min === 0 || field.min) && field.datatype === 'string') {
      validators.push(Validators.minLength(field.min));
    }
    if (typeof field.max !== 'string' && (field.max === 0 || field.max) && field.datatype === 'string') {
      validators.push(Validators.maxLength(field.max));
    }

    if (field.name.toLowerCase().indexOf('email') > -1 && field.control === 'text') {
      if (!field.required && field.visible) {
        validators.push(optionalEmailValidator(formControl));
      } else if (field.required) {
        validators.push(Validators.email);
      }
    }
    if (field.regexp) {
      validators.push(Validators.pattern(field.regexp));
    }
    formControl.setValidators(validators);
    formControl.updateValueAndValidity();
  }

  private subscribeToAltersFormValue(formControl: AbstractControl, field: FieldBase<any>): void {
    this.altersFormSubscriptions.set(
      formControl,
      formControl.valueChanges
        .pipe(
          withLatestFrom(this.formState$),
          map(([val, store]) => {
            // Do not call backend to get new form data if the user is changing state
            if (
              field.name.toLowerCase() === 'countryandstate' &&
              val.country === store.formGroup.value[field.name].country
            ) {
              return {
                type: Action.DEFAULT,
              };
            }
            store.formGroup.updateValueAndValidity();
            const formdata = store.formGroup.value;
            if (field.name.toLowerCase() === 'countryandstate') {
              // Avoid a bug in vform backend handler
              formdata[field.name] = { ...formdata[field.name], state: null };
            }
            return this.formAlteringElementChanged()(field, formdata);
          }),
        )
        .subscribe(),
    );
  }

  protected formStateReducer(state: FormState, action: StoreAction): FormState {
    switch (action.type) {
      case Action.INIT || Action.INIT_VFORM:
        return {
          ...state,
          isLoading: true,
        };
      case Action.GET_FORM_DEF:
        return {
          ...state,
          sections: action.payload,
        };
      case Action.BUILD_FORMGROUP:
        return {
          ...state,
          formGroup: action.payload.formGroup,
          sections: action.payload.sections,
          catalogue: action.payload.catalogue,
          isLoading: false,
        };
      case Action.FORM_READY:
        return {
          ...state,
          formReady: true,
        };
      case Action.FORM_SUBMIT:
        return {
          ...state,
          isSubmitting: true,
        };
      case Action.POST_FORMSUBMIT:
        action.payload.successEventEmitter.emit(true);
        action.payload.successResultEmitter.emit(action.payload.response);
        if (this.formOptions.successUrl) {
          this.router.navigateByUrl(this.formOptions.successUrl);
          return state;
        }
        this.notification$$.next({
          message: 'Success',
          type: NotificationType.SUCCESS,
        });
        return {
          ...state,
          isSubmitting: false,
          onSubmitSuccess: true,
        };
      case Action.ERROR_HANDLING: {
        const message = parseErrorMessage(action.payload);
        this.notification$$.next({
          type: NotificationType.ERROR,
          message,
        });
        return {
          ...state,
          isLoading: false,
          isSubmitting: false,
          onSubmitSuccess: false,
        };
      }
      case Action.ALTERFORM_CHANGED:
        return {
          ...state,
          isLoading: true,
        };
      case Action.UPDATE_FIELD:
        action.payload.forEach((fieldUpdateData: FieldData) => {
          state.sections.forEach((s: Section) => {
            s.fields.forEach((field: FieldBase<any>) => {
              if (field.name === fieldUpdateData.name) {
                field.updateField(fieldUpdateData);
                const currentSubscription = this.altersFormSubscriptions.get(state.formGroup.controls[field.name]);
                if (currentSubscription) {
                  currentSubscription.unsubscribe();
                  this.altersFormSubscriptions.delete(state.formGroup.controls[field.name]);
                }
                state.formGroup.controls[field.name].patchValue(field.value);
                this.applyValidationSettings(state.formGroup.controls[field.name], field);
                if (field.altersForm) {
                  this.subscribeToAltersFormValue(state.formGroup.controls[field.name], field);
                }
              }
            });
          });
        });
        return {
          ...state,
          isLoading: false,
        };
      case Action.CLEAR_ALL:
        state.formGroup.reset();
        return state;
      default:
        return state;
    }
  }

  private ensureObservable(item: StoreAction | FormState): Observable<StoreAction | FormState> {
    return item instanceof Observable ? item : observableFrom([item]);
  }

  private actionDispatcher(func: (...args: any[]) => any): (...args: any[]) => StoreAction {
    return (...args: any[]) => {
      const action: StoreAction = func.call(null, ...args);
      this.action$$.next(action);
      if (action.payload instanceof Observable) {
        this.action$$.next(action.payload);
      }
      return action;
    };
  }

  public getQueryParamsInContext(context: string, formData: any): string {
    const [url, params] = context.split('?');
    if (params) {
      const paramsString = params.split('&').reduce((queryParams, param) => {
        const [key] = param.split('=');
        if (formData.hasOwnProperty(key) && key !== 'pid') {
          return queryParams;
        }
        return queryParams === '' ? (queryParams += param) : (queryParams += `&${param}`);
      }, '');
      return `${url}?${paramsString}`;
    } else {
      return context;
    }
  }
}

export function parseErrorMessage(message: any): string {
  if (typeof message === 'undefined') {
    return '';
  }
  if (message.hasOwnProperty('data')) {
    if (typeof message.data === 'string') {
      return message.data;
    }
    const formError = message.data as FormErrorResponse;

    let errorMessage = '';
    if (formError.fields.length > 1) {
      // TODO: Revisit if this makes sense/makes nice errors.
      errorMessage += 'Multiple errors: ';
    }
    formError.fields.forEach((err) => (errorMessage += err.message + ', '));
    // Slice off the trailing comma
    errorMessage = errorMessage.slice(0, errorMessage.length - 2);
    return errorMessage;
  }
  if (typeof message === 'string') {
    return message;
  }
  if (message.hasOwnProperty('message')) {
    return message.message;
  }
  return 'Unknown error occurred';
}
